"use client"

import { useState } from "react"
import ClipLoader from "react-spinners/ClipLoader"
import { type WorkBook } from "xlsx"
import { FileField } from "../../projects/components/CreateProjectForm/FileField"
import { useColumnTypes } from "../../projects/hooks/useColumnTypes"
import { useDatasetMetadata } from "../../projects/hooks/useDatasetMetadata"
import { useWorkbook } from "../../projects/hooks/useWorkbook"
import type { DataColumn, DataColumnWithType } from "../../projects/types"

export function Calculator() {
    const [file, setFile] = useState<File | null>(null)
    const [workbook, setWorkbook] = useState<WorkBook>()
    const [sheetNames, setSheetNames] = useState<string[]>([])
    const [selectedSheet, setSelectedSheet] = useState<string>("")
    const [allColumns, setAllColumns] = useState<DataColumn[]>([])
    const [dimensions, setDimensions] = useState<{ rows: number; columns: number }>()
    const [columnsWithType, setColumnsWithType] = useState<DataColumnWithType[]>([])

    useWorkbook({
        file,
        setWorkbook,
        setSheetNames,
        setSelectedSheet,
    })

    useDatasetMetadata({
        workbook,
        selectedSheet,
        setAllColumns,
        setDimensions,
    })

    const { loading } = useColumnTypes({
        allColumns,
        dimensions,
        setColumnsWithType,
    })

    const upload = (files: FileList) => {
        if (files) {
            setFile(files[0])
        }
    }

    const reset = () => {
        setFile(null)
        setSheetNames([])
        setSelectedSheet("")
        setAllColumns([])
        setDimensions(undefined)
        setColumnsWithType([])
    }

    if (file == null) return <FileField upload={upload} />

    return (
        <>
            {sheetNames.length > 1 && (
                <div className="col-span-full">
                    <label htmlFor="sheet" className="block leading-6 font-semibold text-gray-900 dark:text-gray-100">
                        Sheet
                    </label>
                    <div className="mt-2">
                        <select
                            id="sheet"
                            name="sheet"
                            className="focus:border-primary-600 focus:ring-primary-600 block w-full rounded-md border-gray-300 py-2 pr-10 pl-3 text-base focus:outline-none sm:text-sm dark:border-gray-800"
                            value={selectedSheet}
                            onChange={(e) => setSelectedSheet(e.target.value)}
                        >
                            <option className="italic" value="">
                                Select a sheet
                            </option>
                            {sheetNames.map((sheetName) => (
                                <option key={sheetName} value={sheetName}>
                                    {sheetName}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            )}
            {loading && columnsWithType.length === 0 && (
                <div className="flex h-full w-full items-center justify-center">
                    <ClipLoader size={40} color="#088d96" />
                </div>
            )}

            {columnsWithType.length > 0 && (
                <>
                    <table className="mt-10 min-w-full divide-y divide-gray-300 dark:divide-gray-800">
                        <thead>
                            <tr>
                                <th
                                    scope="col"
                                    className="py-3.5 pr-3 pl-4 text-left text-sm font-semibold text-gray-900 sm:pl-0 dark:text-gray-100"
                                >
                                    Data type
                                </th>
                                <th
                                    scope="col"
                                    className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100"
                                >
                                    Quantity
                                </th>
                                <th
                                    scope="col"
                                    className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100"
                                >
                                    Cost
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                            <tr>
                                <td className="py-4 pr-3 pl-4 text-sm font-medium whitespace-nowrap text-gray-900 sm:pl-0 dark:text-gray-100">
                                    Text
                                </td>
                                <td className="px-3 py-4 text-sm whitespace-nowrap text-gray-600 dark:text-gray-400">
                                    {columnsWithType
                                        .filter((c) => c.type === "text")
                                        .reduce((acc, c) => acc + c.quantity, 0)
                                        .toLocaleString()}
                                </td>
                                <td className="px-3 py-4 text-sm whitespace-nowrap text-gray-600 dark:text-gray-400">
                                    {columnsWithType
                                        .filter((c) => c.type === "text")
                                        .reduce((acc, c) => acc + c.cost, 0)
                                        .toLocaleString()}{" "}
                                    credits
                                </td>
                            </tr>
                            <tr>
                                <td className="py-4 pr-3 pl-4 text-sm font-medium whitespace-nowrap text-gray-900 sm:pl-0 dark:text-gray-100">
                                    List
                                </td>
                                <td className="px-3 py-4 text-sm whitespace-nowrap text-gray-600 dark:text-gray-400">
                                    {columnsWithType
                                        .filter((c) => c.type === "list")
                                        .reduce((acc, c) => acc + c.quantity, 0)
                                        .toLocaleString()}
                                </td>
                                <td className="px-3 py-4 text-sm whitespace-nowrap text-gray-600 dark:text-gray-400">
                                    {columnsWithType
                                        .filter((c) => c.type === "list")
                                        .reduce((acc, c) => acc + c.cost, 0)
                                        .toLocaleString()}{" "}
                                    credits
                                </td>
                            </tr>
                            <tr>
                                <td className="py-4 pr-3 pl-4 text-sm font-medium whitespace-nowrap text-gray-900 sm:pl-0 dark:text-gray-100">
                                    Unique Identifier
                                </td>
                                <td className="px-3 py-4 text-sm whitespace-nowrap text-gray-600 dark:text-gray-400">
                                    {columnsWithType
                                        .filter((c) => c.type === "id")
                                        .reduce((acc, c) => acc + c.quantity, 0)
                                        .toLocaleString()}
                                </td>
                                <td className="px-3 py-4 text-sm whitespace-nowrap text-gray-600 dark:text-gray-400">
                                    Free
                                </td>
                            </tr>
                            <tr>
                                <td className="py-4 pr-3 pl-4 text-sm font-medium whitespace-nowrap text-gray-900 sm:pl-0 dark:text-gray-100">
                                    Summary report
                                </td>
                                <td className="px-3 py-4 text-sm whitespace-nowrap text-gray-600 dark:text-gray-400">
                                    1
                                </td>
                                <td className="px-3 py-4 text-sm whitespace-nowrap text-gray-600 dark:text-gray-400">
                                    Free
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </>
            )}
        </>
    )
}
